import { Component, inject, OnDestroy, OnInit, AfterViewInit, TemplateRef, ViewChild, Renderer2, ElementRef } from '@angular/core';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { AbstractControl, FormBuilder, FormGroup, isFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Alert, AlertService, AlertType, ModalComponent } from '@michelin/theme';
import {
  UpdateCurrentPaginatedWorkOrdersAction,
  UpdateIsRestoreStateAction,
  WorkOrderState
} from '../../../../shared/state/WorkOrderState';
import { WorkOrder } from '../model/workorder';
import { map, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil, tap } from 'rxjs/operators';
import { PaginatedWOLResponse, WorkOrderHeaderMetadata } from '../work-order-line-list/model/paginated-wol.model';
import { BreadcrumbContextService, BreadcrumbItem } from '../../../../shared/services/breadcrumb-context.service';
import { DealerState, UpdatePlantsAction, UpdateSitesAction, UpdateWorkOrderSettingsAction } from '../../../../shared/state/DealerState';
import { WorkOrderService } from '../../services/workorder.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { RestDealerService } from '../../../../shared/dealer/services/rest-dealer.service';
import { UtilityService } from '../../../../shared/services/utility.service';
import { Site } from '../../../../shared/services/models/Site';
import { Plant } from '../../../plant-setup/model/Plant';
import { PlantService } from '../../../../shared/services/plant.service';
import { SiteService } from '../../../../shared/services/site.service';
import { CustomDateAdapter } from '../../../../shared/services/custom-date-adapter';
import {
  defaultWOFilters,
  EDITABLE,
  ENTERED,
  hasWorkOrderEditRoles,
  isDeletableCasingStatus
} from '../../shared/util/work-order-helper-util';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { WorkOrderLineView } from '../work-order-line-list/model/work-order-line-view.model';
import { DateFormatService } from '../../../../shared/services/date-format.service';
import moment from 'moment-timezone';
import { BulkWoModalComponent } from '../../shared/components/bulk-wo-modal/bulk-wo-modal.component';
import { WoLineModalComponent } from '../../shared/components/wo-line-modal/wo-line-modal.component';
import { ProductionCustomer, SalesmanOption } from '../createworkorder/models/work-order.interfaces';
import { WorkOrderSettings } from '../createworkorder/models/work-order-settings.model';
import { CreateWorkOrderService } from '../createworkorder/create-worker-order.service';
import { environment } from 'src/environments/environment';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from 'src/app/shared/services/featureflags.service';
import { WorkOrderLineListComponent } from '../work-order-line-list/work-order-line-list.component';
import { MatDialog } from '@angular/material/dialog';
import { SharedWarningModalComponent } from '../../../../shared/components/shared-warning-modal/shared-warning-modal.component';
import { SharedDeleteModalComponent } from '../../../../shared/components/shared-delete-modal/shared-dialog.component';
import { WorkOrderFiltersModel } from '../model/work-order-filters.model';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { WorkorderPdfExportService, WorkOrderPdfContext } from '../../services/workorder-pdf-export.service';
import { WorkOrderReportDTO } from '../model/work-order-report.model';

@Component({
  selector: 'app-workorders-details',
  templateUrl: './workorders-details.component.html',
  styleUrl: './workorders-details.component.scss',
  standalone: false
})
export class WorkordersDetailsComponent implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('weekendModal') weekendModal: ModalComponent;
  @ViewChild('weekendConfirmation') weekendConfirmationContent: TemplateRef<any>;
  @ViewChild('customerModalContent') customerModalContent: TemplateRef<any>;
  @ViewChild('customerModal') customerModal: ModalComponent;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;
  @ViewChild('bulkWoModal') bulkWoModal!: BulkWoModalComponent;
  @ViewChild('woLineModal') woLineModal!: WoLineModalComponent;
  @ViewChild('workOrderLineList', { static: false }) workOrderLineList: WorkOrderLineListComponent;

  headerSettings: WorkOrderSettings | null = null;
  productionCustomers: ProductionCustomer[] = [];
  customerRtDot: string = '';
  customerReqTreadDepth: string = '';
  customerDisposalChargesFk: string = '';
  preferredTreadSpecPk: string | null = null;
  customerRememberBrand: string = '';
  customerRememberCasingNote: string = '';

  private readonly createWorkOrderService = inject(CreateWorkOrderService);

  private static readonly dayMap = ['G', 'A', 'B', 'C', 'D', 'E', 'F'];
  workOrderHeaderForm: FormGroup;

  loading: boolean;
  workOrderId: string;
  workOrder: WorkOrder;
  workOrderLines: WorkOrderLineView[];
  dealer: Dealer;
  siteNames: Site[] = [];
  plantNames: Plant[] = [];
  minDate: Date | null = null;
  maxDueDate: Date | null = null;
  minDueDate: Date | null = null;
  isManualReleasedEnabled = false;
  isWorkOrderReleasedEnabled = false;
  isSelectedWOLEnabled = false;
  isCreateWorkOrderEnabled = false;
  isDeleteWorkOrderEnabled = false;

  // Central mapping for status translation keys to keep display consistent with other components
  private readonly STATUS_TRANSLATION_KEYS: { [key: string]: string } = {
    ENTERED: 'enteredText',
    RELEASED: 'releasedText',
    IN_PROGRESS: 'inProgressText',
    COMPLETED: 'completedText',
    CLOSED: 'closedText',
    DELETED: 'deletedText'
  };

  // String arrays for mic-dropdown-search - updated to show 'code - name' format
  plantNamesList: string[] = [];
  siteNamesList: string[] = [];
  salesmen: SalesmanOption[] = [];

  // Store full customer objects for reference
  customers: any[] = [];
  matchedCustomer: any = null;
  selectedCustomerCodePk: string = '';
  selectedCustomerNamePk: string = '';
  selectedPlantPk: string = '';
  selectedSitePk: string = '';

  // Store initial form values for change detection
  private initialFormValues: any = {};
  private isInitialLoad: boolean = true;
  protected receivedDatePickerOpened: boolean = false;

  // Direct customer code lookup state (mirrors createworkorder pattern)
  customerCodeLookupLoading = false;
  private _lastSelectedCustomerCode = '';
  private destroy$ = new Subject<void>();

  canEditFields: boolean = false;
  receiveDtEdit: boolean = false;
  receiveDtPrev: boolean = false;

  // Customer turn around time for conditional checks
  customerTurnAroundTime: number | null = null;

  showDueDayField: boolean = false;
  custCodeReqValue: string | null = null; // 'Y', 'N', or 'R'
  showMfgDotField: boolean = false;
  custCodeFieldLabel: string = 'Ship-to Code'; // legacy (kept if elsewhere referenced)
  custCodeFieldLabelKey: string = 'shipToCodeText'; // new
  private metadataSubscription: Subscription;
  private weekendModalResultSub?: Subscription; // track modal result subscription
  private linesSubscription?: Subscription; // subscription for current work order lines
  hasLockedPosts: boolean = false; // true if any work order line has a non-null lockPost
  metadata: WorkOrderHeaderMetadata | null = null;
  isSaving: boolean = false;
  userLogin: string = '';
  currentDueDayElapsedTime: string;
  private isUserDueDateEdit = false;
  private lastCalculatedDueDate: Date | null = null;

  // Dynamic breadcrumb support
  breadcrumbItems: BreadcrumbItem[] = [];

  // NEW: plant id passed into modal at open time (keeps template binding fresh)
  modalPlantId: string = '';

  // Pagination persistence state: navigation tracking
  private nextNavigationUrl = '';

  columnNames: Record<string, string> = {
    lineNumber: 'Line #',
    casingId: 'Bar Code',
    prodCustomer: 'Production Cust',
    processCode: 'PC',
    workType: 'Work Type',
    casingStatus: 'Status',
    shippingLocation: 'Location',
    post: 'Post',
    make: 'Make',
    series: 'Series',
    size: 'Size',
    treadDesc: 'Tread',
    mdot: 'MFG DOT',
    brand: 'Brand #',
    rejectCode: 'Reject Code',
    repairs: 'Repairs'
  };

  exportDataFetcher = (): Observable<ExportResponse> => {
    return this.workOrderService.searchWorkOrderLines(this.dealer.id, this.workOrderId, 1, 10000).pipe(
      map((response: PaginatedWOLResponse) => {
        let lines = response.data.workOrderLines as Record<string, any>[];
        if (this.dealer.displayDeleteCasing === 'N') {
          lines = lines.filter((line) => line['casingStatus'] !== 'DELETED');
        }
        return { data: lines };
      })
    );
  };

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: 'WO-' + (this.workOrder?.workOrderNumber || 'export'),
      extraColumns: this.extraColumns,
      pdfExportCallback: () => this.exportToPdf(),
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  exportToPdf(): void {
    this.loading = true;
    this.workOrderService.getWorkOrderDetails(this.dealer.id, this.workOrderId).subscribe({
      next: (report: WorkOrderReportDTO) => {
        const lines = report.workOrderLines || [];
        // Associate Dealer Customer is provided per line in the API; surface it in the header when present
        const associateDealerCustomer = (report.associateDealerCustomer || lines[0]?.associateDealerCustomer || '').trim();
        const context: WorkOrderPdfContext = {
          plantName: report.plant || '',
          siteName: report.site || '',
          dealerName: this.dealer?.name || '',
          createdBy: report.createdBy || '',
          customerAddress: report.customerAddress || '',
          customerCity: '',
          customerState: '',
          customerZip: '',
          contact: report.contact || '',
          zoneImage: report.zoneImage || '',
          associateDealerCustomer: associateDealerCustomer
        };
        // Map the report DTO back to WorkOrder shape for the PDF service
        const woForPdf: WorkOrder = {
          ...this.workOrder,
          workOrderNumber: report.workOrderNumber || this.workOrder.workOrderNumber,
          customerCode: report.customerId || this.workOrder.customerCode,
          crn: this.workOrder.crn,
          customerName: this.workOrder.customerName,
          numberOfLines: report.totalTags ?? this.workOrder.numberOfLines,
          receivedDate: report.date || this.workOrder.receivedDate
        };
        this.workorderPdfExportService.generateWorkOrderPdf(woForPdf, lines as any[], context);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.utilityService.showAlert(AlertType.critical, this.translationService.translate('errorText'), 'Failed to export PDF.');
      }
    });
  }

  // Return translated label for a status code using central map, fallback to raw status
  public getTranslatedStatus(status?: string): string {
    if (!status) return '';
    const key = (status || '').trim().toUpperCase().replace(/\s+/g, '_');
    const translationKey = this.STATUS_TRANSLATION_KEYS[key];
    return translationKey ? this.translationService.translate(translationKey) : status;
  }

  public translationService = inject(TranslationService);
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public breadcrumbContextService = inject(BreadcrumbContextService);
  public alertService = inject(AlertService);
  public plantService = inject(PlantService);
  public SiteService = inject(SiteService);
  public workOrderService = inject(WorkOrderService);
  public restDealerService = inject(RestDealerService);
  public utilityService = inject(UtilityService);
  public dateAdapter = inject(CustomDateAdapter);
  public refreshWolList = 0;
  public dateFormatService = inject(DateFormatService);
  public featureflagsService = inject(FeatureflagsService);
  public dialog = inject(MatDialog);
  public workorderPdfExportService = inject(WorkorderPdfExportService);
  public elementRef = inject(ElementRef);
  public renderer = inject(Renderer2);

  constructor() {
    this.showDueDayField = false;
    this.custCodeReqValue = null;
    this.showMfgDotField = false;
    this.custCodeFieldLabel = 'Ship-to Code';
    this.custCodeFieldLabelKey = 'shipToCodeText';
    this.userLogin = this.store.selectSnapshot(DealerState.currentUser);

    this.workOrderHeaderForm = this.fb.group({
      workOrderNumber: ['', { validators: [Validators.required, Validators.maxLength(10)] }],
      workOrderStatus: [''],
      numberOfLines: [''],
      customerCode: ['', Validators.required],
      customerName: ['', Validators.required],
      crn: [''],
      plant: ['', Validators.required],
      site: ['', Validators.required],
      receivedDate: [''],
      dueDate: ['', Validators.required],
      customerNote: [''],
      dueDay: [''],
      custCode: [''],
      fiscalIdNum: [''],
      salesman: ['']
    });
    this.loading = true;
    this.workOrderId = this.route.snapshot.paramMap.get('workOrderId');
  }

  ngOnInit(): void {
    this.loading = true;
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.createWorkOrderService.getSalesmen(this.dealer.id).subscribe({
      next: (salesmen) => {
        this.salesmen = salesmen;
        if (this.workOrder) {
          this.workOrderHeaderForm.patchValue({ salesman: this.getSalesmanSelection(this.workOrder.salesmanFk) }, { emitEvent: false });
        }
      },
      error: () => (this.salesmen = [])
    });
    this.isManualReleasedEnabled =
      this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.MANUAL_RELEASED_ENABLED) &&
      hasWorkOrderEditRoles() &&
      this.dealer.markWoAvailable == 'Y';
    this.isCreateWorkOrderEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.CREATE_WORK_ORDER_ENABLED);
    this.isDeleteWorkOrderEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.DELETE_WORK_ORDER_ENABLED);

    this.setReceivedDateBounds();

    // First load plants and sites, then work order details
    Promise.all([this.fetchPlants(), this.fetchSites()]).then(() => {
      this.fetchWorkOrderDetails();
      this.loading = false;
    });

    this.subscribeToMetadata();
    this.workOrderLines = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrderLines);
    this.subscribeToWorkOrderLines();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.nextNavigationUrl = (event as NavigationStart).url;
      });

    this.setupCustomerFieldSyncListeners();

    // Debounced direct lookup by customer code (mirrors createworkorder pattern)
    this.workOrderHeaderForm
      .get('customerCode')
      ?.valueChanges.pipe(
        tap((value: string) => {
          if (!value) this.clearLookupError('customerCode');
        }),
        debounceTime(1500),
        distinctUntilChanged(),
        filter((value: string) => !!value && value !== this._lastSelectedCustomerCode),
        takeUntil(this.destroy$)
      )
      .subscribe((value: string) => {
        if (this.canEditFields) {
          this.lookupCustomerByCode(value);
        }
      });

    let previousDueDate: Date | null = this.workOrderHeaderForm.get('dueDate')?.value;
    // Listen for receivedDate changes and update dueDate
    this.workOrderHeaderForm.get('receivedDate')?.valueChanges.subscribe((newReceivedDate) => {
      // Flag received dates outside the dealer's back-date limit as invalid whenever the
      // value changes, regardless of whether it came from the picker or manual typed entry
      this.updateBackDateLimitError(this.workOrderHeaderForm.get('receivedDate'), newReceivedDate);

      if (this.isSaving || this.isInitialLoad) {
        return;
      }

      this.updateDueDateBasedOnReceivedDate(newReceivedDate);
      this.setDueDateBoundsFromReceivedDate();
    });

    // Listen for dueDate changes and update related fields (except dueDate calculation and weekend logic)
    this.workOrderHeaderForm.get('dueDate')?.valueChanges.subscribe((newDueDate) => {
      if (!this.isInitialLoad) {
        this.lastCalculatedDueDate = previousDueDate;
        this.isUserDueDateEdit = true;
      }
      this.updateDueDateRelatedFieldsOnDueDateChange(newDueDate);
      previousDueDate = newDueDate;
    });
  }

  ngAfterViewInit(): void {
    // Pagination state is now handled by mic-table's built-in storage mechanism
    // Force sync of paginator page indicator after first data load
    this.workOrderLineList?.tableRef?.paginator?.dataChanged.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(() => this.syncPaginatorPageIndicator());
    });
  }

  private syncPaginatorPageIndicator(): void {
    // mic-table may briefly show page 1 after data length updates; force one resync.
    const paginator = this.workOrderLineList?.tableRef?.paginator;
    const targetIndex = paginator?.pageIndex ?? 0;

    if (!paginator || !targetIndex) {
      return;
    }

    // Toggle page index on both paginator refs to refresh visual selected-page state.
    const paginatorRefs = [paginator.paginatorRef1, paginator.paginatorRef2].filter(Boolean);
    paginatorRefs.forEach((ref) => {
      ref.pageIndex = 0;
    });

    setTimeout(() => {
      paginatorRefs.forEach((ref) => {
        ref.pageIndex = targetIndex;
      });
    });
  }

  get extraColumns(): Record<string, string> {
    return {
      'WO #': this.workOrder?.workOrderNumber ?? '',
      'Site': this.workOrder?.site ?? '',
      'Plant': this.workOrder?.plant ?? '',
      'Received': this.workOrder?.receivedDate ?? '',
      'Due': this.workOrder?.dueDate ?? '',
      'Customer': this.workOrder?.customerName ?? '',
      'CRN': this.workOrder?.crn ?? ''
    };
  }

  onWolListFetchComplete() {
    this.checkIsHeaderEditable();
  }

  private checkIsHeaderEditable() {
    // Get feature flags
    const featureFlags = this.store.selectSnapshot(DealerState.featureFlags);

    const isStatusEditable =
      !!this.workOrder &&
      (this.workOrder.workOrderStatus === 'RELEASED' ||
        this.workOrder.workOrderStatus === 'ENTERED' ||
        (this.workOrder.workOrderStatus === 'IN_PROGRESS' && this.hasQualifyingCasings()));
    this.canEditFields = !!(
      featureFlags?.flags?.['isEditWorkOrderEnabled'] &&
      isStatusEditable &&
      hasWorkOrderEditRoles() &&
      this.workOrder.anyCasingLocked === false
    );

    if (!this.metadata.isWorkOrderEditable) {
      this.canEditFields = false;
    }
  }

  /**
   * Setup dynamic validation for receivedDate based on receiveDtEdit flag
   */
  private setupReceivedDateValidation(): void {
    const plantCode = this.getSelectedPlantCode();

    if (plantCode) {
      const plantObj = this.plantNames.find((p) => p.code === plantCode);
      this.receiveDtEdit = !!(plantObj && plantObj.receiveDtEdit === 'Y');
    } else {
      this.receiveDtEdit = false;
    }

    // Ensure validators reflect the resolved flag
    this.updateReceivedDateValidator();
  }

  /**
   * Update receivedDate field validators based on receiveDtEdit flag
   */
  private updateReceivedDateValidator(): void {
    const receivedDateControl = this.workOrderHeaderForm.get('receivedDate');
    if (receivedDateControl) {
      if (this.receiveDtEdit) {
        receivedDateControl.setValidators([Validators.required]);
      } else {
        receivedDateControl.clearValidators();
      }
      receivedDateControl.updateValueAndValidity();
    }
  }

  /**
   * Update fiscalIdNum field validators based on custCodeReqValue
   */
  private updateFiscalIdNumValidator(): void {
    const fiscalIdNumControl = this.workOrderHeaderForm.get('fiscalIdNum');
    if (fiscalIdNumControl) {
      if (this.custCodeReqValue === 'R') {
        fiscalIdNumControl.setValidators([Validators.required]);
      } else {
        fiscalIdNumControl.clearValidators();
      }
      fiscalIdNumControl.updateValueAndValidity();
    }
  }

  /**
   * Setup listeners to sync customer code and customer name
   */
  private setupCustomerFieldSyncListeners(): void {
    // Listen to plant changes to update receivedDate validation
    this.workOrderHeaderForm.get('plant')?.valueChanges.subscribe((selectedPlant) => {
      if (selectedPlant) {
        this.onPlantChange(selectedPlant);
      }
    });
  }

  /**
   * Handle plant selection - update receiveDtEdit flag and validators
   */
  onPlantChange(selectedPlant: string): void {
    if (selectedPlant && selectedPlant.includes('-')) {
      const plantCode = selectedPlant.split('-')[0].trim();
      const plantObj = this.plantNames.find((plant) => plant.code === plantCode);
      if (plantObj) {
        this.receiveDtEdit = plantObj.receiveDtEdit === 'Y';
        this.receiveDtPrev = plantObj.receiveDtPrev === 'Y';
        this.currentDueDayElapsedTime = plantObj.dueDayElapsedTime;

        this.workOrderHeaderForm.get('customerCode').markAsTouched();
        this.workOrderHeaderForm.get('customerName').markAsTouched();

        // Reset receivedDate field to original work order value whenever plant changes
        if (this.workOrder?.receivedDate) {
          const originalDate = this.parseWODate(this.workOrder.receivedDate);
          this.workOrderHeaderForm.get('receivedDate')?.setValue(originalDate);
        } else {
          this.workOrderHeaderForm.get('receivedDate')?.setValue(null);
        }

        // Update validators based on new receiveDtEdit value
        this.updateReceivedDateValidator();

        // Clear customer/plant association error when plant changes
        this.clearCustomerPlantAssociationError();
      }
    }
  }

  /**
   * Handle when received date datepicker is opened
   */
  onReceivedDatePickerOpened(): void {
    this.receivedDatePickerOpened = true;
  }

  /**
   * Handle when received date datepicker is closed
   */
  onReceivedDatePickerClosed(): void {
    this.receivedDatePickerOpened = false;
  }

  /**
   * Check if there is at least one casing with status 'RELEASED' or 'SHOP_FLOOR'
   */
  private hasQualifyingCasings(): boolean {
    if (!this.workOrderLines) {
      return true;
    }

    return this.workOrderLines.some((line) => line.casingStatus === 'RELEASED' || line.casingStatus === 'SHOP_FLOOR');
  }

  fetchWorkOrderDetails(): void {
    const workOrders = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders);
    this.workOrder = workOrders.find((wo) => wo.id === this.workOrderId);

    if (!this.workOrder) {
      this.fetchWorkOrderDetailsFromServer();
      return;
    }

    this.populateWorkOrderDetails();
  }

  /**
   * Fallback fetch used when the work order is not yet present in the cached paginated list
   * (e.g. immediately after creation). Re-queries the backend filtered by this work order's ID
   * and updates the shared store, then populates the header form from the result.
   */
  private fetchWorkOrderDetailsFromServer(): void {
    const filters: WorkOrderFiltersModel = { ...defaultWOFilters, workOrderHeaderPk: this.workOrderId };
    this.workOrderService
      .refreshWorkOrders(this.dealer.id, filters, 1, 10, 'workOrderNumber:desc')
      .pipe(take(1))
      .subscribe({
        next: () => {
          const workOrders = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders);
          this.workOrder = workOrders.find((wo) => wo.id === this.workOrderId);

          if (!this.workOrder) {
            this.workOrderHeaderForm.reset();
            this.updateUIControlStates();
            return;
          }

          this.populateWorkOrderDetails();
        },
        error: (error) => {
          console.error('Failed to fetch newly created work order details:', error);
          this.workOrderHeaderForm.reset();
          this.updateUIControlStates();
        }
      });
  }

  private populateWorkOrderDetails(): void {
    this.isWorkOrderReleasedEnabled = this.workOrder.workOrderStatus === ENTERED;
    this.isSelectedWOLEnabled = this.workOrder.containsEditableCasing;
    this.updateUIControlStates();

    // Find the plant object and convert to 'code - name' format
    if (this.workOrder.plant && this.plantNames.length > 0) {
      const plantCode = this.workOrder.plant.split('-')[0];
      const plantName = this.workOrder.plant.split('-')[1];
      const plantObj = this.plantNames.find((p) => p.id === plantCode || p.name === plantName);
      if (plantObj) {
        this.receiveDtEdit = plantObj.receiveDtEdit === 'Y';
        this.receiveDtPrev = plantObj.receiveDtPrev === 'Y';
        this.updateReceivedDateValidator();
      }
    }

    const format = this.dateFormatService.currentFormat;

    this.workOrderHeaderForm.patchValue(
      {
        ...this.workOrder,
        numberOfLines: (this.dealer.displayDeleteCasing === 'N' && this.isDeleteWorkOrderEnabled
          ? (this.workOrder.numberOfLines || 0) - (this.workOrder.deletedCasingCount || 0)
          : this.workOrder.numberOfLines || 0
        ).toString(),
        // keep the raw status code in the form control (translation handled in the template)
        workOrderStatus: this.workOrder?.workOrderStatus || '',
        receivedDate: this.workOrder.receivedDate ? moment(this.parseWODate(this.workOrder.receivedDate)).format(format) : null,
        dueDate: this.workOrder.dueDate ? moment(this.parseWODate(this.workOrder.dueDate)).format(format) : null,
        fiscalIdNum: this.metadata ? this.metadata.fiscalIdNum : '',
        salesman: this.getSalesmanSelection(this.workOrder?.salesmanFk)
      },
      { emitEvent: false }
    );

    this.setReceivedDateBounds();
    this.setDueDateBoundsFromReceivedDate();

    // Setup received date validation after form is populated
    this.setupReceivedDateValidation();

    this.setupDynamicBreadcrumbs();

    // Load modal context data now that workOrder is available
    this.loadHeaderSettings();
    this.loadProductionCustomersForModal();

    this.isSaving = false;
    this.isInitialLoad = false;
  }

  private loadHeaderSettings(): void {
    this.headerSettings = this.store.selectSnapshot(DealerState.workOrderSettings);

    if (!this.headerSettings) {
      this.restDealerService.getWorkOrderSettings(this.dealer.id).subscribe({
        next: (response) => {
          this.headerSettings = response.data;
          this.store.dispatch(new UpdateWorkOrderSettingsAction(this.headerSettings));
        },
        error: (error) => {
          console.error('Failed to load header settings:', error);
          this.headerSettings = {} as WorkOrderSettings;
        }
      });
    }
  }

  private loadProductionCustomersForModal(): void {
    const customerPk = this.selectedCustomerCodePk || this.workOrder?.customerPk;
    if (!customerPk || !this.dealer?.id) return;
    this.createWorkOrderService.getProductionCustomers(this.dealer.id, customerPk).subscribe({
      next: (data) => {
        this.productionCustomers = data;
        this.loadCustomerSettings(customerPk);
      },
      error: () => (this.productionCustomers = [])
    });
  }

  private loadCustomerSettings(customerPk: string): void {
    if (!customerPk || !this.dealer?.id) return;
    const plantId = this.evaluatePlantId();
    this.workOrderService.getCustomersForDropdown(this.dealer.id, plantId).subscribe({
      next: (customers) => {
        const customer = customers.find((c: any) => c.customerPk === customerPk || c.id === customerPk);
        if (customer) {
          this.matchedCustomer = customer;
          this.customerRtDot = customer.rtDot || customer.customerRtDot || '';
          this.customerReqTreadDepth = customer.reqTreadDepth || customer.customerReqTreadDepth || '';
          this.customerDisposalChargesFk = customer.disposalChargesFk || customer.customerDisposalChargesFk || '';
          this.preferredTreadSpecPk = customer.preferredTreadSpecPk || null;
          this.customerRememberBrand = customer.rememberBrand || '';
          this.customerRememberCasingNote = customer.rememberCasingNote || '';
        }
      },
      error: (err) => {
        console.error('Failed to load customer settings:', err);
        this.customerRtDot = '';
        this.customerReqTreadDepth = '';
        this.customerDisposalChargesFk = '';
        this.preferredTreadSpecPk = null;
        this.customerRememberBrand = '';
        this.customerRememberCasingNote = '';
      }
    });
  }

  /**
   * Populate form with work order data after customers are loaded
   */
  private populateFormWithWorkOrderData(plantDisplayValue: string, siteDisplayValue: string): void {
    const formData = {
      ...this.workOrder,
      plant: plantDisplayValue,
      site: siteDisplayValue,
      workOrderStatus: this.workOrder?.workOrderStatus || '',
      receivedDate: this.parseWODate(this.workOrder.receivedDate),
      dueDate: this.parseWODate(this.workOrder.dueDate),
      dueDay: this.metadata ? this.metadata.dueDayValue : '',
      custCode: this.metadata ? this.metadata.fiscalIdNum : '',
      salesman: this.getSalesmanSelection(this.workOrder?.salesmanFk)
    };

    this.workOrderHeaderForm.patchValue(formData, { emitEvent: false });
    // Store initial values for change detection
    this.initialFormValues = { ...this.workOrderHeaderForm.value };

    // Set the initial customer turn around time using form control values
    const selectedName = this.workOrderHeaderForm.get('customerName')?.value;
    const matchingCustomer = this.customers.find((customer) => customer.customerName === selectedName);
    this.customerTurnAroundTime = matchingCustomer?.turnAroundTime ?? null;

    // Mark initial load as complete so subsequent plant changes will trigger customer fetch
    this.isInitialLoad = false;

    // Setup breadcrumbs after work order is loaded
    this.setupDynamicBreadcrumbs();
  }

  private getSalesmanSelection(value: string | SalesmanOption | null | undefined): SalesmanOption | string {
    if (!value) return '';
    if (typeof value !== 'string') return value;
    return this.salesmen.find((salesman) => salesman.id === value || salesman.login === value) || value;
  }

  private getSalesmanId(value: string | SalesmanOption | null | undefined): string {
    if (value && typeof value === 'object') return value.id;
    return typeof value === 'string' ? value : '';
  }

  getSalesmanDisplay(value: string | SalesmanOption | null | undefined): string {
    if (!value) return '';
    return typeof value === 'string' ? this.salesmen.find((salesman) => salesman.id === value)?.login || value : value.login;
  }

  private subscribeToMetadata(): void {
    this.metadataSubscription = this.store.select(WorkOrderState.getWorkOrderHeaderMetadata).subscribe((getMetadata) => {
      if (this.workOrderId) {
        const metadata = getMetadata(this.workOrderId);
        if (metadata) {
          this.metadata = metadata;
          this.updateUIControlStates();
          this.updateFormData();
        }
      }
    });
  }

  /**
   * Subscribe to currentPaginatedWorkOrderLines to detect lockPost presence
   */
  private subscribeToWorkOrderLines(): void {
    this.linesSubscription = this.store.select(WorkOrderState.currentPaginatedWorkOrderLines).subscribe((lines) => {
      if (!lines || !Array.isArray(lines)) {
        this.hasLockedPosts = false;
      } else {
        // A lockPost is considered present when its value is not null/empty
        const anyLocked = lines.some((line: any) => line && line.lockPost != null && line.lockPost !== '');
        this.hasLockedPosts = anyLocked;
      }
    });
  }

  saveChanges() {
    if (this.custCodeReqValue === 'R' && !this.workOrderHeaderForm.get('fiscalIdNum')?.value) {
      this.utilityService.showAlert(
        AlertType.critical,
        this.translationService.translate('incorrectValuesText'),
        this.translationService.translate('cnpjErrorText')
      );
      return;
    } else if (this.workOrderHeaderForm.get('receivedDate')?.hasError('backDateLimit')) {
      this.loading = false;
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translationService.translate('errorText'),
        content: this.translationService.translate('dueDatePastErrorText').replace('-', `${this.dealer?.woBackDtLimit ?? 30}`)
      } as Alert);
      return;
    } else if (this.workOrderHeaderForm.get('dueDate')?.hasError('weekendNotAllowed')) {
      this.loading = false;
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translationService.translate('errorText'),
        content: this.translationService.translate('dueDateWeekendNotAllowedText')
      } as Alert);
      return;
    } else if (this.isDueDateLessThanReceivedDateFromForm()) {
      this.loading = false;
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translationService.translate('errorText'),
        content: this.translationService.translate('dueDateOnOrBeforeReceivedDateText')
      } as Alert);
      return;
    } else if (this.workOrderHeaderForm.get('dueDate')?.hasError('matDatepickerMin')) {
      this.loading = false;
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translationService.translate('errorText'),
        content: this.translationService.translate('dueDateMinErrorText').replace('-', `${this.getTurnaroundTime()}`)
      } as Alert);
      return;
    } else if (this.workOrderHeaderForm.get('dueDate')?.hasError('matDatepickerMax')) {
      this.loading = false;
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translationService.translate('errorText'),
        content: this.translationService.translate('dueDateFutureErrorText').replace('-', `${this.dealer?.dueDateOverrideLimit ?? 0}`)
      } as Alert);
      return;
    } else if (!this.workOrderHeaderForm.valid) {
      this.utilityService.showAlert(
        AlertType.strong,
        this.translationService.translate('incorrectValuesText'),
        this.translationService.translate('incorrectValuesOnWOHeaderText')
      );
      return;
    }

    this.loading = true;

    const dueDateValue = this.workOrderHeaderForm.get('dueDate')?.value;
    const receiveDate = new Date(this.workOrderHeaderForm.get('receivedDate')?.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (receiveDate > today && !hasWorkOrderEditRoles()) {
      this.loading = false;
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translationService.translate('errorText'),
        content: this.translationService.translate('receiveDateSelectText')
      } as Alert);
      return;
    }

    if (dueDateValue) {
      const dueDate = new Date(dueDateValue);
      let isLessThanTurnAroundTime = false;
      // if we don't have customer turn around time yet, get it from work order or api and wait for API call
      this.customerTurnAroundTime =
        this.customerTurnAroundTime == null
          ? this.workOrder && this.workOrder.customerTurnAroundTime != null
            ? this.workOrder.customerTurnAroundTime
            : 0
          : this.customerTurnAroundTime;
      const recDatePlusTurnTime = receiveDate;
      // adding turn around time days to received date
      recDatePlusTurnTime.setDate(recDatePlusTurnTime.getDate() + this.customerTurnAroundTime);
      isLessThanTurnAroundTime = recDatePlusTurnTime > dueDate;

      if (!isNaN(dueDate.getTime())) {
        if (dueDate < receiveDate || dueDate < today || isLessThanTurnAroundTime) {
          this.loading = false;
          this.alertService.showAlert({
            type: AlertType.critical,
            title: this.translationService.translate('errorText'),
            content: this.translationService.translate('dueDateValidationText')
          } as Alert);
          return;
        }

        if (this.isWeekend(dueDate) && this.isUserDueDateEdit) {
          // Show modal confirmation for weekend due dates
          this.weekendModal.open({
            id: 'weekend-confirmation',
            width: 665,
            title: this.translationService.translate('warningText'),
            content: this.weekendConfirmationContent,
            confirm: 'Ok',
            cancel: 'Cancel'
          });

          // Clean previous subscription to avoid accumulation
          if (this.weekendModalResultSub) {
            this.weekendModalResultSub.unsubscribe();
          }
          // Subscribe once to modal result
          this.weekendModalResultSub = this.weekendModal.result.pipe(take(1)).subscribe((result: any) => {
            if (result && result.result) {
              this.proceedWithSave();
            } else {
              this.loading = false;

              if (this.lastCalculatedDueDate) {
                this.workOrderHeaderForm.get('dueDate')?.setValue(this.lastCalculatedDueDate, { emitEvent: false });
                this.workOrderHeaderForm.get('dueDay')?.setValue(WorkordersDetailsComponent.dayMap[this.lastCalculatedDueDate.getDay()]);
              }
            }
          });
          return;
        }
      }
    }

    // Proceed with save if no weekend issue
    this.proceedWithSave();
  }

  openCustomerModal(): void {
    // Compute the plant id to pass into the modal at open time, using the currently selected plant in the form
    this.modalPlantId = this.evaluatePlantId();

    this.customerModal.open({
      id: 'customer-select',
      width: 800,
      title: 'Select Customer',
      content: this.customerModalContent,
      confirm: 'Close'
    });
  }

  /**
   * Proceed with saving the work order
   */
  private proceedWithSave(): void {
    const dueDateValue = this.workOrderHeaderForm.get('dueDate')?.value;

    // Begin loading state for global spinner
    this.loading = true;
    this.isSaving = true;

    // Updated logic to extract plant ID from 'code - name' format
    const currentPlant = this.workOrderHeaderForm.get('plant').value;
    if (currentPlant && currentPlant.includes('-')) {
      const plantCode = currentPlant.split('-')[0].trim();
      this.selectedPlantPk = this.plantNames.find((plant) => plant.code === plantCode)?.id;
    } else {
      this.selectedPlantPk = this.plantNames.find((plant) => plant.name === currentPlant)?.id;
    }

    // Updated logic to extract site ID from 'code - name' format
    const currentSite = this.workOrderHeaderForm.get('site').value;
    if (currentSite && currentSite.includes('-')) {
      const siteCode = currentSite.split('-')[0].trim();
      this.selectedSitePk = this.siteNames.find((site) => site.code === siteCode)?.id;
    } else {
      this.selectedSitePk = this.siteNames.find((site) => site.name === currentSite)?.id;
    }

    const woId = this.workOrderId ? this.workOrderId : 'new';
    const dueDateObj = moment(dueDateValue, this.dateFormatService.currentFormat).toDate();
    const recDateObj = moment(this.workOrderHeaderForm.get('receivedDate')?.value, this.dateFormatService.currentFormat).toDate();
    const payload = {
      workOrderNum: this.workOrderHeaderForm.get('workOrderNumber').value,
      crn: this.workOrderHeaderForm.get('crn').value,
      dueDate: Date.UTC(dueDateObj.getFullYear(), dueDateObj.getMonth(), dueDateObj.getDate(), 12, 0, 0, 0),
      receivedDate: Date.UTC(recDateObj.getFullYear(), recDateObj.getMonth(), recDateObj.getDate(), 12, 0, 0, 0),
      plantFk: this.selectedPlantPk,
      siteFk: this.selectedSitePk,
      customerFk: this.selectedCustomerCodePk
        ? this.selectedCustomerCodePk
        : this.workOrder.customerPk != null || this.workOrder.customerPk != undefined
        ? this.workOrder.customerPk
        : null,
      dealerFk: this.dealer.id,
      createdBy: null,
      updatedBy: this.userLogin,
      workOrderHeaderPk: woId,
      fiscalIdNum: this.metadata ? this.metadata.fiscalIdNum : '',
      salesmanFk: this.getSalesmanId(this.workOrderHeaderForm.get('salesman')?.value)
    };

    this.workOrderService.updateWorkOrderHeader(this.dealer.id, woId, payload).subscribe({
      next: (res) => {
        this.isSaving = false;
        const errors = res?.data?.errors;
        if (errors && errors.length > 0) {
          // End loading state if there are validation errors
          this.loading = false;
          errors.forEach((error: any) => {
            // Handle customer/plant validation errors with custom message
            if (error.errorCode === 'CUSTOMER_NOT_ASSOCIATED_WITH_PLANT') {
              this.setCustomerPlantAssociationError();
              this.alertService.showAlert({
                type: AlertType.critical,
                title: this.translationService.translate('errorText'),
                content: this.translationService.translate('plantCustomerErrorText')
              } as Alert);
            } else {
              this.utilityService.showMappedAlert(error.errorCode);
            }
          });
          return;
        }
        const responseData = res?.data?.data;
        if (!responseData) {
          // Missing expected payload structure
          this.loading = false;
          this.utilityService.showMappedAlert('GENERIC_ERROR');
          return;
        }

        const currentPlant = this.workOrderHeaderForm.get('plant').value;
        const currentSite = this.workOrderHeaderForm.get('site').value;
        const updatedWorkOrder: WorkOrder = {
          ...this.workOrder,
          workOrderNumber: res.data.data.workOrderNumber,
          customerName: res.data.data.customerName,
          customerPk: res.data.data.customerPk,
          customerCode: res.data.data.customerCode,
          crn: res.data.data.crn,
          plant: currentPlant,
          site: currentSite,
          dueDate: res.data.data.dueDate,
          receivedDate: res.data.data.receivedDate,
          customerNote: res.data.data.customerNote,
          fiscalIdNum: res.data.data.fiscalIdNum != null ? res.data.data.fiscalIdNum : this.metadata ? this.metadata.fiscalIdNum : '',
          salesmanFk: this.getSalesmanId(this.workOrderHeaderForm.get('salesman')?.value)
        };

        const currentList = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders);
        const recordCount = this.store.selectSnapshot(WorkOrderState.totalRecords);
        const updatedList = currentList.map((wo) => (wo.id === updatedWorkOrder.id ? updatedWorkOrder : wo));
        this.store.dispatch(new UpdateCurrentPaginatedWorkOrdersAction(updatedList, recordCount));

        this.workOrder = updatedWorkOrder;
        this.fetchWorkOrderDetails();
        this.refreshWolList++;
        this.loading = false;
        this.utilityService.showMappedAlert('WORK_ORDER_UPDATE_SUCCESS');
      },
      error: () => {
        this.isSaving = false;
        this.loading = false;
        this.utilityService.showMappedAlert('WORK_ORDER_UPDATE_FAIL');
      }
    });
  }

  private formatDateOnly(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private saveNewLine(line: any): void {
    if (!line) {
      return;
    }

    this.loading = true;

    const receivedDate = this.parseWODate(this.workOrder.receivedDate);
    const dueDate = this.parseWODate(this.workOrder.dueDate);

    const saveRequest = {
      editRequest: true,
      header: {
        workOrderNumber: this.workOrder.workOrderNumber,
        plantId: this.selectedPlantPk || this.evaluatePlantId(),
        siteId: this.selectedSitePk || this.evaluateSiteId(),
        receivedDate: receivedDate ? this.formatDateOnly(receivedDate) : '',
        dueDate: dueDate ? this.formatDateOnly(dueDate) : ''
      },
      workOrderLines: [this.serializeLineForSave(line)],
      dealerId: this.dealer.id,
      createdBy: null,
      modifiedBy: this.userLogin,
      workOrderHeaderPk: this.workOrderId
    };

    this.createWorkOrderService.saveWorkOrder(saveRequest, this.dealer.id).subscribe({
      next: (response: any) => {
        const actualResponse = response.data || response;

        if (actualResponse?.errors && actualResponse.errors.length > 0) {
          this.loading = false;
          actualResponse.errors.forEach((error: any) => {
            this.utilityService.showMappedAlert(error.errorCode || 'GENERIC_ERROR');
          });
          return;
        }

        const rawNumberOfLines = this.workOrder.numberOfLines || 0;
        const updatedRawNumberOfLines =
          line.lineType.toLowerCase() === 'bulk' ? rawNumberOfLines + (line.totalLines || 0) : rawNumberOfLines + 1;
        this.workOrder = { ...this.workOrder, numberOfLines: updatedRawNumberOfLines, containsEditableCasing: true };
        this.isSelectedWOLEnabled = this.workOrder.containsEditableCasing;
        const displayValue =
          this.dealer.displayDeleteCasing === 'N' && this.isDeleteWorkOrderEnabled
            ? updatedRawNumberOfLines - (this.workOrder.deletedCasingCount || 0)
            : updatedRawNumberOfLines;
        this.workOrderHeaderForm.get('numberOfLines')?.setValue(displayValue);

        const currentList = this.store.selectSnapshot(WorkOrderState.currentPaginatedWorkOrders);
        const recordCount = this.store.selectSnapshot(WorkOrderState.totalRecords);
        const updatedList = currentList.map((wo) => (wo.id === this.workOrder.id ? this.workOrder : wo));
        this.store.dispatch(new UpdateCurrentPaginatedWorkOrdersAction(updatedList, recordCount));

        this.refreshWolList++;
        this.loading = false;

        this.utilityService.showAlert(
          AlertType.success,
          this.translationService.translate('successUpdateGroupText') || 'Success',
          this.translationService.translate('successLineText')
        );
      },
      error: (error) => {
        this.loading = false;
        console.error('Save work order line error:', error);
        this.utilityService.showMappedAlert('WORK_ORDER_LINE_SAVE_FAIL');
      }
    });
  }

  private serializeLineForSave(line: any): any {
    const toId = (value: any): string => {
      if (!value) return '';
      if (typeof value === 'object' && value.id) {
        return value.id;
      }
      return value || '';
    };

    return {
      lineType: line.lineType,
      id: line.id,
      lineNumber: line.lineNumber,
      barcode: line.barcode,
      casingLocation: line.casingLocation,
      casingStatus: line.casingStatus,
      shippingLocation: line.shippingLocation,
      workType: line.workType,
      post: line.post,
      woSite: line.woSite,
      processCodeId: toId(line.processCodeId),
      processCode:
        (line.processCodeId as any)?.code ||
        (typeof line.processCodeId === 'string' && !line.processCodeId.startsWith('process-') ? line.processCodeId : ''),
      productionCustomerId: toId(line.productionCustomerId),
      customerCode: line.customerCode,
      customerName: line.customerName,
      receivedDate: line.receivedDate,
      completeDate: line.completeDate,
      dueDate: line.dueDate,
      makeId: toId(line.makeId),
      seriesId: toId(line.seriesId),
      sizeId: toId(line.sizeId),
      treadId: toId(line.treadId),
      application: line.application,
      axle: line.axle,
      treadWidth: line.treadWidth,
      speedIndex: line.speedIndex,
      ntrId: toId(line.ntrId),
      moldType: line.moldType,
      mfgDot: line.mfgDot,
      rtDot: line.rtDot,
      brandNumber: line.brandNumber,
      sequenceNumber: line.sequenceNumber,
      repair: line.repair,
      chamberNum: line.chamberNum,
      minCircumference: line.minCircumference,
      maxCircumference: line.maxCircumference,
      casingNotes: line.casingNotes,
      camSpec: line.camSpec,
      systemGrade: line.systemGrade,
      overrideGrade: line.overrideGrade,
      wheelType: line.wheelType,
      wheelColor: line.wheelColor,
      scrapCodeId: line.scrapCodeId || null,
      npCodeId: line.npCodeId || null,
      disposalChargeId: line.disposalChargeId || null,
      sendToServicePostFlag: line.sendToServicePostFlag || 'N',
      treadDepthMin: line.treadDepthMin || null,
      treadDepthMax: line.treadDepthMax || null,
      casingValue: line.casingValue || null,
      lengthId: toId(line.lengthId) || null,
      ignoreCheckCamSpecNtr: line.ignoreCheckCamSpecNtr || null,
      ignoreCheckCamSpecAge: line.ignoreCheckCamSpecAge || null,
      rejectInd: line.rejectInd || null,
      camViolationField: line.camViolationField || null,
      camViolationDisposition: line.camViolationDisposition || null,
      bulkId: line.bulkId,
      totalLines: line.totalLines || null
    };
  }

  private static optionalPattern = (pattern: RegExp, key: string): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = control.value;
      if (v === null || v === undefined || v === '') return null;
      return pattern.test(String(v)) ? null : ({ [key]: true } as ValidationErrors);
    };
  };

  private updateFormData(): void {
    if (this.workOrder) {
      const formData = {
        ...this.workOrder,
        numberOfLines: (this.dealer.displayDeleteCasing === 'N' && this.isDeleteWorkOrderEnabled
          ? (this.workOrder.numberOfLines || 0) - (this.workOrder.deletedCasingCount || 0)
          : this.workOrder.numberOfLines || 0
        ).toString(),
        workOrderStatus: this.workOrder?.workOrderStatus || '',
        receivedDate: this.parseWODate(this.workOrder.receivedDate),
        dueDate: this.parseWODate(this.workOrder.dueDate),
        dueDay: this.metadata ? this.metadata.dueDayValue : '',
        custCode: this.metadata ? this.metadata.fiscalIdNum : '',
        customerName: this.workOrder.customerName || '',
        customerCode: this.workOrder.customerCode || '',
        fiscalIdNum: this.metadata.fiscalIdNum || '',
        salesman: this.getSalesmanSelection(this.workOrder.salesmanFk)
      };

      this.workOrderHeaderForm.patchValue(formData, { emitEvent: false });
      this.initialFormValues = { ...this.workOrderHeaderForm.value };
    }
  }

  private updateUIControlStates(): void {
    if (this.metadata) {
      this.showDueDayField = this.metadata.showDueDayField;
      this.custCodeReqValue = this.metadata.custCodeReqValue || null;
      const key = this.getLabelFromId(this.metadata.custCodeLabelId);
      this.custCodeFieldLabelKey = key;
      // maintain legacy string (optional fallback)
      this.custCodeFieldLabel = this.translationService.translate(key) || 'Ship-to Code';

      // Update fiscalIdNum validators based on custCodeReqValue
      this.updateFiscalIdNumValidator();
    } else {
      this.showDueDayField = false;
      this.custCodeReqValue = null;
      this.custCodeFieldLabelKey = 'shipToCodeText';
      this.custCodeFieldLabel = 'Ship-to Code';

      // Clear validators when no metadata
      this.updateFiscalIdNumValidator();
    }

    // Apply CRN field requirement based on zone
    // CRN is required ONLY for SA and EU zone
    const crnControl = this.workOrderHeaderForm.get('crn');
    if (crnControl) {
      if (environment.envName.includes('sa') || environment.envName.includes('eu')) {
        // SA zone - CRN is required with max length 20
        crnControl.setValidators([Validators.required, Validators.maxLength(20)]);
      } else {
        // NA zone - CRN is optional with max length 20
        crnControl.setValidators([Validators.maxLength(20)]);
      }
      crnControl.updateValueAndValidity();
    }
  }

  private setupDynamicBreadcrumbs(): void {
    // Clear casing search context if this is normal navigation (not from casing search)
    // This prevents "Casing List" from showing when navigating from work order menu
    const currentContext = this.breadcrumbContextService.getNavigationContext();
    if (!currentContext || currentContext.source !== 'casing-search') {
      this.breadcrumbContextService.clearNavigationContext();
    }

    // Generate breadcrumbs based on navigation context
    // Pass workOrderId to ensure correct navigation even from breadcrumbs
    // Set fromWorkOrderMenu to true since this is accessed from work order menu navigation
    this.breadcrumbItems = this.breadcrumbContextService.generateBreadcrumbs(
      'work-order-details',
      this.workOrder?.workOrderNumber,
      undefined, // casingId not needed for work order details
      this.workOrder?.id, // Pass the work order ID for proper breadcrumb navigation
      true // fromWorkOrderMenu = true for normal work order navigation
    );
  }

  private getLabelFromId(labelId?: string): string {
    switch (labelId) {
      case 'CNPJ':
        return this.translationService.translate('cnpjText'); // expects translation key
      case 'SHIP_TO_CODE':
        return this.translationService.translate('shipToCodeText');
      default:
        return this.translationService.translate('shipToCodeText');
    }
  }

  /**
   * Set customer/plant association error on form controls
   */
  private setCustomerPlantAssociationError(): void {
    // Add custom error to plant, customerCode, and customerName controls
    const plantControl = this.workOrderHeaderForm.get('plant');
    const customerCodeControl = this.workOrderHeaderForm.get('customerCode');
    const customerNameControl = this.workOrderHeaderForm.get('customerName');

    if (plantControl) {
      plantControl.setErrors({ ...plantControl.errors, incorrect: true });
      plantControl.markAsTouched();
    }
    if (customerCodeControl) {
      customerCodeControl.setErrors({ ...customerCodeControl.errors, incorrect: true });
      customerCodeControl.markAsTouched();
    }
    if (customerNameControl) {
      customerNameControl.setErrors({ ...customerNameControl.errors, incorrect: true });
      customerNameControl.markAsTouched();
    }
  }

  /**
   * Clear customer/plant association error from form controls
   */
  private clearCustomerPlantAssociationError(): void {
    // Remove custom error from plant, customerCode, and customerName controls
    const plantControl = this.workOrderHeaderForm.get('plant');
    const customerCodeControl = this.workOrderHeaderForm.get('customerCode');
    const customerNameControl = this.workOrderHeaderForm.get('customerName');

    if (plantControl && plantControl.errors) {
      delete plantControl.errors['incorrect'];
      if (Object.keys(plantControl.errors).length === 0) {
        plantControl.setErrors(null);
      } else {
        plantControl.updateValueAndValidity({ emitEvent: false });
      }
    }
    if (customerCodeControl && customerCodeControl.errors) {
      delete customerCodeControl.errors['incorrect'];
      if (Object.keys(customerCodeControl.errors).length === 0) {
        customerCodeControl.setErrors(null);
      } else {
        customerCodeControl.updateValueAndValidity({ emitEvent: false });
      }
    }
    if (customerNameControl && customerNameControl.errors) {
      delete customerNameControl.errors['incorrect'];
      if (Object.keys(customerNameControl.errors).length === 0) {
        customerNameControl.setErrors(null);
      } else {
        customerNameControl.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  canDeactivate(): boolean | Promise<boolean> {
    return canDeactivateForm(
      this.isFormChanged.bind(this),
      this.unsavedChangesModal,
      this.unsavedChangesContentModal,
      this.translationService
    );
  }

  private isFormChanged(): boolean {
    return JSON.stringify(this.initialFormValues) !== JSON.stringify(this.workOrderHeaderForm.value);
  }

  get hasUnsavedHeaderChanges(): boolean {
    return this.isFormChanged();
  }

  backNavigation(): void {
    (globalThis as any).history.back();
  }

  /**
   * Clear pagination localStorage entries when navigating away from allowed routes
   */
  private clearPaginationStorage(): void {
    // Remove mic-table pagination storage entries using the workorder-line-list prefix
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('workorder-line-list-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  /**
   * Determine if pagination state should be retained based on navigation destination
   */
  private shouldRetainPaginationStateForNavigation(nextUrl: string): boolean {
    if (!nextUrl) {
      return false;
    }
    // Retain state only when navigating to work order line details
    return nextUrl.startsWith('/home/work-orders/work-order-line-details');
  }

  ngOnDestroy(): void {
    // Clear pagination storage if navigation is outside allowed routes
    if (!this.shouldRetainPaginationStateForNavigation(this.nextNavigationUrl)) {
      this.clearPaginationStorage();
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.store.dispatch(new UpdateIsRestoreStateAction(true));
    if (this.metadataSubscription) {
      this.metadataSubscription.unsubscribe();
    }
    if (this.weekendModalResultSub) {
      this.weekendModalResultSub.unsubscribe();
    }
    if (this.linesSubscription) {
      this.linesSubscription.unsubscribe();
    }
  }

  fetchPlants(): Promise<void> {
    return new Promise((resolve) => {
      const plants = this.store.selectSnapshot(DealerState.plants);
      if (plants && plants.length > 0) {
        this.plantNames = plants; // Array of plant objects
        this.plantNamesList = plants.map((plant) => `${plant.code} - ${plant.name}`); // Populate string array for mic-dropdown-search with 'code - name' format
        this.currentDueDayElapsedTime = plants[0].dueDayElapsedTime;
        resolve();
      } else {
        this.createWorkOrderService.getWorkOrderInitData(this.dealer.id).subscribe({
          next: (data) => {
            const plantsForStore = data.plants as any;
            this.store.dispatch(new UpdatePlantsAction(plantsForStore));
            this.plantNames = data.plants as any;
            this.plantNamesList = data.plants.map((plant) => `${plant.code} - ${plant.name}`);
            this.currentDueDayElapsedTime = data.plants[0]?.dueDayElapsedTime || '0';
            resolve();
          },
          error: (err) => {
            console.error('Failed to load plants:', err);
            // Fallback to empty arrays
            this.plantNames = [];
            this.plantNamesList = [];
            resolve();
          }
        });
      }
    });
  }

  fetchSites(): Promise<void> {
    return new Promise((resolve) => {
      const sites = this.store.selectSnapshot(DealerState.sites);
      if (sites && sites.length > 0) {
        this.siteNames = sites; // Array of site objects
        this.siteNamesList = sites.map((site) => `${site.code} - ${site.name}`); // Populate string array for mic-dropdown-search with 'code - name' format
        resolve();
      } else {
        this.SiteService.getSites(this.dealer.id).subscribe((sites) => {
          this.store.dispatch(new UpdateSitesAction(sites));
          this.siteNames = sites; // Array of site objects
          this.siteNamesList = sites.map((site) => `${site.code} - ${site.name}`); // Populate string array for mic-dropdown-search with 'code - name' format
          resolve();
        });
      }
    });
  }

  onCustomerSelected(customer: any): void {
    if (!customer) return;

    this.selectedCustomerCodePk = customer.customerPk;
    this.selectedCustomerNamePk = customer.customerPk;
    this.customerTurnAroundTime = customer.turnAroundTime ?? null;

    this.workOrderHeaderForm.patchValue(
      {
        customerCode: customer.code,
        customerName: customer.customerName,
        fiscalIdNum: customer.fiscalIdNum
      },
      { emitEvent: false }
    );

    // Track the value so the debounced lookup doesn't re-fire
    this._lastSelectedCustomerCode = customer.code || '';
    this.clearLookupError('customerCode');

    const receivedDate = this.parseDate(this.workOrderHeaderForm.get('receivedDate')?.value);

    if (receivedDate) {
      this.workOrderHeaderForm.get('receivedDate')?.setValue(receivedDate, { emitEvent: false });

      this.updateDueDateBasedOnReceivedDate(receivedDate);

      this.updateDueDateBoundsFromCustomer();
    }

    this.clearCustomerPlantAssociationError();

    this.loadProductionCustomersForModal();

    this.customerModal.dialog.closeAll();
  }

  /** Remove the lookupFailed error from a field, restoring validator-driven errors. */
  private clearLookupError(fieldName: string): void {
    const ctrl = this.workOrderHeaderForm.get(fieldName);
    if (!ctrl || !ctrl.hasError('lookupFailed')) return;
    const errorsCopy = { ...(ctrl.errors as any) };
    delete errorsCopy.lookupFailed;
    ctrl.setErrors(Object.keys(errorsCopy).length > 0 ? errorsCopy : null);
  }

  /**
   * Look up a customer by typing directly into the customerCode field.
   * On a successful match the customer fields are populated just like onCustomerSelected().
   * On no match an inline lookupFailed error is shown.
   */
  private lookupCustomerByCode(searchCode: string): void {
    if (!this.dealer?.id || !searchCode?.trim()) return;

    this.customerCodeLookupLoading = true;
    this.clearLookupError('customerCode');

    this.createWorkOrderService
      .lookupCustomerByCode(
        this.dealer.id,
        this.userLogin,
        this.modalPlantId || this.evaluatePlantId(),
        searchCode.trim(),
        false,
        true,
        'CUSTOMER,ASSOCIATE_DEALER'
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((customer: any) => {
        this.customerCodeLookupLoading = false;
        if (customer) {
          this.onCustomerSelected(customer);
        } else {
          const msg = `No customer found for Customer Code "${searchCode}".`;
          this._lastSelectedCustomerCode = searchCode;
          this.workOrderHeaderForm.patchValue({ customerName: '' }, { emitEvent: false });
          const ctrl = this.workOrderHeaderForm.get('customerCode');
          ctrl?.setErrors({ ...(ctrl.errors ?? {}), lookupFailed: msg });
          ctrl?.markAsTouched();
        }
      });
  }

  evaluatePlantId() {
    const plantValue = this.workOrderHeaderForm.get('plant')?.value;
    let computedPlantId = '';

    if (plantValue) {
      if (typeof plantValue === 'string' && plantValue.includes('-')) {
        const plantCode = plantValue.split('-')[0].trim();
        const plantObj = this.plantNames.find((p) => p.code === plantCode || p.name === plantValue);
        if (plantObj) {
          computedPlantId = plantObj.id;
        }
      } else {
        // plantValue may already be the display name or id—try matching by name or id
        const plantObj = this.plantNames.find((p) => p.name === plantValue || p.id === plantValue);
        if (plantObj) {
          computedPlantId = plantObj.id;
        }
      }
    }
    return computedPlantId;
  }

  evaluateSiteId() {
    const siteValue = this.workOrderHeaderForm.get('site')?.value;
    let computedSiteId = '';

    if (siteValue) {
      if (typeof siteValue === 'string' && siteValue.includes('-')) {
        const siteCode = siteValue.split('-')[0].trim();
        const siteObj = this.siteNames.find((s) => s.code === siteCode || s.name === siteValue);
        if (siteObj) {
          computedSiteId = siteObj.id;
        }
      } else {
        // siteValue may already be the display name or id—try matching by name or id
        const siteObj = this.siteNames.find((s) => s.name === siteValue || s.id === siteValue);
        if (siteObj) {
          computedSiteId = siteObj.id;
        }
      }
    }
    return computedSiteId;
  }

  // -------- Modal accessors --------

  openAddBulkLines(): void {
    this.bulkWoModal?.open();
  }

  openAddLine(): void {
    this.woLineModal?.open();
  }

  onBulkLinesAdded(newLines: any[]): void {
    console.log('Bulk lines added:', newLines);
    if (!newLines?.length) return;

    const rep = newLines.reduce((a, b) => {
      const aNum = parseInt(a?.lineNumber || '0', 10) || 0;
      const bNum = parseInt(b?.lineNumber || '0', 10) || 0;
      return aNum <= bNum ? a : b;
    }, newLines[0]);

    const lineWithBulk = {
      ...rep,
      lineNumber: (this.workOrder.numberOfLines || 0) + 1,
      bulkId: rep.bulkId,
      totalLines: newLines.length
    };

    this.saveNewLine(lineWithBulk);
  }

  onLineAdded(line: any): void {
    if (!line) {
      return;
    }
    this.saveNewLine(line);
  }

  get modalPlant(): any {
    const plantId = this.evaluatePlantId();
    return this.plantNames.find((p) => p.id === plantId) ?? null;
  }

  get modalCustomerUuid(): string {
    return this.selectedCustomerCodePk || this.workOrder?.customerPk || '';
  }

  /**
   * Check if a date falls on a weekend (Saturday or Sunday)
   */
  private isWeekend(date: Date): boolean {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    return weekday === 'Saturday' || weekday === 'Sunday';
  }

  protected dueDateFilter = (date: Date | null): boolean => {
    if (!date) return true;
    if (this.getAllowWeekendFlag(this.getSelectedPlantCode()) !== 'N') return true;
    return !this.isWeekend(date);
  };

  protected isDueDateLessThanReceivedDateFromForm(): boolean {
    const receivedDateValue = this.workOrderHeaderForm.get('receivedDate')?.value;
    const dueDateValue = this.workOrderHeaderForm.get('dueDate')?.value;

    const receivedDate = this.parseDate(receivedDateValue);
    const dueDate = this.parseDate(dueDateValue);

    if (!receivedDate || !dueDate) {
      return false;
    }

    return dueDate <= receivedDate;
  }

  private parseWODate(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    return this.dateAdapter.parse(dateStr) as Date | null;
  }

  private updateDueDateBasedOnReceivedDate(newReceivedDate: Date | string): void {
    if (this.isSaving) {
      return;
    }
    const plantCode = this.getSelectedPlantCode();
    let elapsedDays = this.getTurnaroundTime();
    if (elapsedDays === 0) {
      elapsedDays = this.getPlantElapsedDays(plantCode);
    }
    const receivedDate = this.parseDate(newReceivedDate);
    if (!receivedDate) return;

    const dueDate = this.calculateDueDate(receivedDate, elapsedDays);
    this.isUserDueDateEdit = false;
    this.workOrderHeaderForm.get('dueDate')?.setValue(dueDate);
    this.workOrderHeaderForm.get('dueDay')?.setValue(WorkordersDetailsComponent.dayMap[dueDate.getDay()]);
  }

  private updateDueDateRelatedFieldsOnDueDateChange(newDueDate: Date | string): void {
    const plantCode = this.getSelectedPlantCode();
    const allowWeekendFlag = this.getAllowWeekendFlag(plantCode);

    const dueDate = this.parseDate(newDueDate);
    if (!dueDate) return;

    const turnaroundTime = this.getTurnaroundTime();
    const receivedDate = this.parseDate(this.workOrderHeaderForm.get('receivedDate')?.value);
    const dueDayControl = this.workOrderHeaderForm.get('dueDay');
    const dueDateControl = this.workOrderHeaderForm.get('dueDate');

    // Flag weekend due dates as invalid whenever the plant setting disallows them,
    // regardless of whether the value came from auto-calculation or manual selection.
    this.updateWeekendNotAllowedError(dueDateControl, dueDate, allowWeekendFlag);

    if (!this.isDueDateValid(dueDate, receivedDate, turnaroundTime)) {
      dueDayControl?.setValue('Z');
      dueDayControl?.setErrors({ invalidDueDate: true });
      return;
    }

    const deliveryDay = WorkordersDetailsComponent.dayMap[dueDate.getDay()];

    if ('FG'.includes(deliveryDay) && allowWeekendFlag === 'N') {
      this.workOrderHeaderForm.get('dueDay')?.setValue('X');
    } else {
      this.workOrderHeaderForm.get('dueDay')?.setValue(deliveryDay);
    }

    dueDayControl?.setErrors(null);
  }

  private updateWeekendNotAllowedError(dueDateControl: AbstractControl | null, dueDate: Date, allowWeekendFlag: string | null): void {
    if (!dueDateControl) return;

    const isWeekendNotAllowed = allowWeekendFlag === 'N' && this.isWeekend(dueDate);
    const currentErrors = dueDateControl.errors;

    if (isWeekendNotAllowed) {
      dueDateControl.setErrors({ ...currentErrors, weekendNotAllowed: true });
      return;
    }

    if (currentErrors && currentErrors['weekendNotAllowed']) {
      const remainingErrors = { ...currentErrors };
      delete remainingErrors['weekendNotAllowed'];
      dueDateControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }

  private getSelectedPlantCode(): string | undefined {
    const selectedPlant = this.workOrderHeaderForm.get('plant')?.value;
    return selectedPlant?.split('-')[0]?.trim();
  }

  private getPlantElapsedDays(plantCode: string | undefined): number {
    const plant = this.plantNames.find((p) => p.code === plantCode);
    return plant ? parseInt(plant.dueDayElapsedTime, 10) || 0 : 0;
  }

  private getAllowWeekendFlag(plantCode: string | undefined): string {
    const plant = this.plantNames.find((p) => p.code === plantCode);
    return plant ? plant.allowDueDateOnWeekEnd : null;
  }

  private parseDate(date: Date | string | null): Date | null {
    return date instanceof Date ? date : this.parseWODate(date);
  }

  private calculateDueDate(receivedDate: Date, elapsedDays: number): Date {
    const dueDate = new Date(receivedDate);
    dueDate.setDate(dueDate.getDate() + elapsedDays);
    const plantCode = this.getSelectedPlantCode();

    if (this.getAllowWeekendFlag(plantCode) === 'N') {
      if (dueDate.getDay() === 6) dueDate.setDate(dueDate.getDate() + 2); // Saturday
      if (dueDate.getDay() === 0) dueDate.setDate(dueDate.getDate() + 1); // Sunday
    }

    return dueDate;
  }

  protected getTurnaroundTime(): number {
    return this.customerTurnAroundTime ?? this.workOrder?.customerTurnAroundTime ?? 0;
  }

  private isDueDateValid(dueDate: Date, receivedDate: Date | null, turnaroundTime: number): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (receivedDate) {
      const minDueDate = new Date(receivedDate);
      minDueDate.setDate(minDueDate.getDate() + turnaroundTime);

      return dueDate >= minDueDate && dueDate >= today;
    }

    return dueDate >= today;
  }

  private setReceivedDateBounds(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const backLimit = this.dealer?.woBackDtLimit;

    if (typeof backLimit === 'number' && backLimit >= 0) {
      const min = new Date(today);
      min.setDate(today.getDate() - backLimit);
      this.minDate = min;
    } else {
      this.minDate = null;
    }
  }

  private updateBackDateLimitError(receivedDateControl: AbstractControl | null, newReceivedDate: Date | string | null): void {
    if (!receivedDateControl) return;

    const receivedDate = this.parseDate(newReceivedDate);
    const currentErrors = receivedDateControl.errors;
    const isBackDateLimitExceeded = !!receivedDate && !!this.minDate && receivedDate < this.minDate;

    if (isBackDateLimitExceeded) {
      receivedDateControl.setErrors({ ...currentErrors, backDateLimit: true });
      return;
    }

    if (currentErrors && currentErrors['backDateLimit']) {
      const remainingErrors = { ...currentErrors };
      delete remainingErrors['backDateLimit'];
      receivedDateControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }

  private setDueDateBoundsFromReceivedDate(): void {
    const receivedDate = this.parseDate(this.workOrderHeaderForm.get('receivedDate')?.value);
    const overrideLimit = this.dealer?.dueDateOverrideLimit;

    if (!receivedDate) {
      this.maxDueDate = null;
      this.minDueDate = null;
      return;
    }

    // Set minDueDate based on customer turnaround time
    const turnaround = this.getTurnaroundTime();
    this.minDueDate = new Date(receivedDate);
    this.minDueDate.setDate(this.minDueDate.getDate() + turnaround);
    // Set maxDueDate based on dealer override limit
    if (typeof overrideLimit === 'number' && overrideLimit >= 0) {
      const max = new Date(receivedDate);
      max.setDate(max.getDate() + overrideLimit);
      max.setHours(0, 0, 0, 0);
      this.maxDueDate = max;
    } else {
      this.maxDueDate = null;
    }
  }

  private updateDueDateBoundsFromCustomer(): void {
    const receivedDate = this.parseDate(this.workOrderHeaderForm.get('receivedDate')?.value);
    if (!receivedDate) return;

    const turnaround = this.getTurnaroundTime();
    this.workOrderHeaderForm.patchValue({ dueDate: this.calculateDueDate(receivedDate, turnaround) }, { emitEvent: false });

    this.minDueDate = new Date(receivedDate);
    this.minDueDate.setDate(this.minDueDate.getDate() + turnaround);

    // Still respect dealer override cap if present
    if (typeof this.dealer?.dueDateOverrideLimit === 'number') {
      if (turnaround > this.dealer.dueDateOverrideLimit) {
        this.maxDueDate = this.minDueDate;
      } else {
        const max = new Date(receivedDate);
        max.setDate(max.getDate() + this.dealer.dueDateOverrideLimit);
        this.maxDueDate = max;
      }
    }
  }

  protected readonly isFormGroup = isFormGroup;

  onSelectedWOLReleasedClick(): void {
    const selectedWOLines = this.workOrderLineList?.selectedWorkOrderLines ?? [];
    if (selectedWOLines.length < 1) {
      this.dialog
        .open(SharedWarningModalComponent, {
          data: {
            title: this.translationService.translate('warningText'),
            message: this.translationService.translate('selectAtLeastOneItemText'),
            buttonText: this.translationService.translate('okText')
          }
        })
        .afterClosed();
      return;
    }

    const editableCasings = selectedWOLines.filter((line) => line.casingStatus == EDITABLE);
    if (editableCasings.length < 1) {
      this.dialog
        .open(SharedWarningModalComponent, {
          data: {
            title: this.translationService.translate('warningText'),
            message: this.translationService.translate('onlyEditableCanBeReleasedText'),
            buttonText: this.translationService.translate('okText')
          }
        })
        .afterClosed();
      return;
    } else {
      this.workOrderService
        .markSelectedWOLReleased(
          this.dealer.id,
          this.workOrderId,
          selectedWOLines.map((line) => line.id),
          this.userLogin
        )
        .subscribe({
          next: () => {
            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('wolReleasedSuccessfulText')
            } as Alert);

            this.refreshWolList++;

            const filters: WorkOrderFiltersModel = { ...defaultWOFilters, workOrderHeaderPk: this.workOrderId };
            this.workOrderService
              .refreshWorkOrders(this.dealer.id, filters, 1, 10, 'workOrderNumber:desc')
              .pipe(take(1))
              .subscribe(() => this.fetchWorkOrderDetails());
          },
          error: () =>
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('wolReleasedFailedText')
            } as Alert)
        });
    }
  }

  canDeleteSelection(): boolean {
    const selectedWOLines = this.workOrderLineList?.selectedWorkOrderLines ?? [];
    return selectedWOLines.length > 0 && selectedWOLines.every((line) => isDeletableCasingStatus(line.casingStatus));
  }

  onDelete(): void {
    if (!this.canDeleteSelection()) {
      return;
    }

    this.dialog
      .open(SharedDeleteModalComponent, {
        data: {
          title: this.translationService.translate('deleteWorkOrderLineText'),
          messageTitle: this.translationService.translate('warningText'),
          message: this.translationService.translate('deleteWorkOrderLineDialogText'),
          buttonText: this.translationService.translate('yesDeleteText')
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result !== true) {
          return;
        }

        const selectedWOLines = this.workOrderLineList?.selectedWorkOrderLines ?? [];
        const itemsToDelete = selectedWOLines.map((line) => line.id).filter((id): id is string => !!id);
        if (itemsToDelete.length === 0) {
          return;
        }

        this.workOrderService.markDeleteAndSaveCasingsBulk(this.dealer.id, itemsToDelete, this.userLogin).subscribe({
          next: (resp) => {
            const success = resp?.data?.isSuccess ?? resp?.isSuccess ?? true;
            this.alertService.showAlert({
              type: success ? AlertType.success : AlertType.strong,
              content: this.translationService.translate(success ? 'deleteSuccessfulText' : 'deleteOperationFailedText')
            } as Alert);

            if (success) {
              this.refreshWolList++;
              const filters: WorkOrderFiltersModel = { ...defaultWOFilters, workOrderHeaderPk: this.workOrderId };
              this.workOrderService
                .refreshWorkOrders(this.dealer.id, filters, 1, 10, 'workOrderNumber:desc')
                .pipe(take(1))
                .subscribe(() => this.fetchWorkOrderDetails());
            }
          },
          error: (err) => {
            console.error('Bulk delete failed:', err);
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('deleteOperationFailedText')
            } as Alert);
          }
        });
      });
  }

  onWorkOrderReleasedClick(): void {
    this.workOrderService.markWorkOrderReleased(this.dealer.id, this.workOrderId, this.userLogin).subscribe({
      next: () => {
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('workOrderReleasedSuccessfulText')
        } as Alert);

        this.refreshWolList++;

        const filters: WorkOrderFiltersModel = { ...defaultWOFilters, workOrderHeaderPk: this.workOrderId };
        this.workOrderService
          .refreshWorkOrders(this.dealer.id, filters, 1, 10, 'workOrderNumber:desc')
          .pipe(take(1))
          .subscribe(() => this.fetchWorkOrderDetails());
      },
      error: () =>
        this.alertService.showAlert({
          type: AlertType.strong,
          content: this.translationService.translate('workOrderReleasedFailedText')
        } as Alert)
    });
  }

  public getNextLineNumber(): number {
    return (this.workOrder?.numberOfLines ?? 0) + 1;
  }

  private addCustomerTooltip(): void {
    try {
      const customerCodeControl = this.elementRef.nativeElement.querySelector('#customer-code-field');
      if (customerCodeControl) {
        this.renderer.setAttribute(
          customerCodeControl,
          'title',
          this.translationService.translate('cannotModifyCustomerWhileInProgressText')
        );
        this.renderer.setStyle(customerCodeControl, 'cursor', 'help');
      }
      const customerNameControl = this.elementRef.nativeElement.querySelector('#customer-name-field');
      if (customerNameControl) {
        this.renderer.setAttribute(
          customerNameControl,
          'title',
          this.translationService.translate('cannotModifyCustomerWhileInProgressText')
        );
        this.renderer.setStyle(customerNameControl, 'cursor', 'help');
      }
    } catch (error) {
      console.warn('Failed adding customer tooltip', error);
    }
  }
}
